import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Driver Comparison Widget
 * Side-by-side driver statistics comparison tool
 * Compare career stats, current season performance, and head-to-head
 */
const F1DriverComparison = ({ season = 2025 }) => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver1, setSelectedDriver1] = useState(null);
  const [selectedDriver2, setSelectedDriver2] = useState(null);
  const [driver1Stats, setDriver1Stats] = useState(null);
  const [driver2Stats, setDriver2Stats] = useState(null);
  const [loading, setLoading] = useState(true);
 
  // Fetch all drivers
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        setLoading(true);
        const response = await f1Service.getCurrentDrivers();

        if (response && response.response) {
          const sortedDrivers = response.response.sort((a, b) => {
            return a.name.localeCompare(b.name);
          });
          setDrivers(sortedDrivers);

          // Auto-select top 2 drivers if available
          if (sortedDrivers.length >= 2) {
            setSelectedDriver1(sortedDrivers[0].id);
            setSelectedDriver2(sortedDrivers[1].id);
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching drivers:', err);
        setLoading(false);
      }
    };

    fetchDrivers();
  }, [season]);

  // Fetch driver 1 detailed stats
  useEffect(() => {
    if (!selectedDriver1) return;

    const fetchDriver1Stats = async () => {
      try {
        const response = await f1Service.getDriverById(selectedDriver1);
        if (response && response.response && response.response.length > 0) {
          setDriver1Stats(response.response[0]);
        }
      } catch (err) {
        console.error('Error fetching driver 1 stats:', err);
      }
    };

    fetchDriver1Stats();
  }, [selectedDriver1]);

  // Fetch driver 2 detailed stats
  useEffect(() => {
    if (!selectedDriver2) return;

    const fetchDriver2Stats = async () => {
      try {
        const response = await f1Service.getDriverById(selectedDriver2);
        if (response && response.response && response.response.length > 0) {
          setDriver2Stats(response.response[0]);
        }
      } catch (err) {
        console.error('Error fetching driver 2 stats:', err);
      }
    };

    fetchDriver2Stats();
  }, [selectedDriver2]);

  // Get team color
  const getTeamColor = (driver) => {
    if (!driver || !driver.teams) return '#666666';
    const currentTeam = driver.teams.find(t => t.season === season);
    if (!currentTeam) return '#666666';
    const colors = f1Service.getTeamColors(currentTeam.team?.name);
    return colors.primary;
  };

  // Compare stat values and return winner indicator
  const compareStats = (val1, val2, higherIsBetter = true) => {
    const num1 = parseFloat(val1) || 0;
    const num2 = parseFloat(val2) || 0;

    if (num1 === num2) return { winner: 'tie', value1: val1, value2: val2 };

    if (higherIsBetter) {
      return {
        winner: num1 > num2 ? 'driver1' : 'driver2',
        value1: val1,
        value2: val2
      };
    } else {
      return {
        winner: num1 < num2 ? 'driver1' : 'driver2',
        value1: val1,
        value2: val2
      };
    }
  };

  // Stat row component
  const StatRow = ({ label, val1, val2, higherIsBetter = true }) => {
    const comparison = compareStats(val1, val2, higherIsBetter);

    return (
      <div className="grid grid-cols-3 gap-4 py-3 border-b border-white/5">
        {/* Driver 1 Value */}
        <div className="text-right">
          <span
            className={`font-bold tabular-nums ${
              comparison.winner === 'driver1'
                ? 'text-white'
                : comparison.winner === 'tie'
                ? 'text-gray-500'
                : 'text-gray-600'
            }`}
          >
            {comparison.value1 || '—'}
          </span>
        </div>

        {/* Stat Label */}
        <div className="text-center">
          <span className="text-[10px] text-gray-600 uppercase tracking-wider font-mono">
            {label}
          </span>
        </div>

        {/* Driver 2 Value */}
        <div className="text-left">
          <span
            className={`font-bold tabular-nums ${
              comparison.winner === 'driver2'
                ? 'text-white'
                : comparison.winner === 'tie'
                ? 'text-gray-500'
                : 'text-gray-600'
            }`}
          >
            {comparison.value2 || '—'}
          </span>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-40 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4">
          <div className="h-64 bg-white/10 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  // Empty state
  if (drivers.length < 2) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          Not Enough Drivers to Compare
        </p>
      </div>
    );
  }

  const driver1Color = getTeamColor(driver1Stats);
  const driver2Color = getTeamColor(driver2Stats);

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
          Driver Comparison
        </h3>
      </div>

      {/* Driver Selectors */}
      <div className="grid grid-cols-2 gap-4 p-4 border-b border-white/5">
        {/* Driver 1 Selector */}
        <div className="space-y-2">
          <label className="text-[10px] text-gray-600 uppercase tracking-wider font-mono">
            Driver 1
          </label>
          <select
            value={selectedDriver1 || ''}
            onChange={(e) => setSelectedDriver1(parseInt(e.target.value))}
            className="w-full bg-white/5 border border-white/10 text-white text-xs font-mono p-2 rounded-sm focus:outline-none focus:border-white/20"
            style={{ borderColor: driver1Color + '40' }}
          >
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id} className="bg-black">
                {driver.name}
              </option>
            ))}
          </select>
        </div>

        {/* Driver 2 Selector */}
        <div className="space-y-2">
          <label className="text-[10px] text-gray-600 uppercase tracking-wider font-mono">
            Driver 2
          </label>
          <select
            value={selectedDriver2 || ''}
            onChange={(e) => setSelectedDriver2(parseInt(e.target.value))}
            className="w-full bg-white/5 border border-white/10 text-white text-xs font-mono p-2 rounded-sm focus:outline-none focus:border-white/20"
            style={{ borderColor: driver2Color + '40' }}
          >
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id} className="bg-black">
                {driver.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Comparison View */}
      {driver1Stats && driver2Stats && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {/* Driver Headers */}
          <div className="grid grid-cols-2 gap-4 p-4 border-b border-white/5">
            {/* Driver 1 Card */}
            <div
              className="p-3 border rounded-sm"
              style={{
                backgroundColor: `${driver1Color}10`,
                borderColor: `${driver1Color}40`
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ backgroundColor: driver1Color }}
                />
                <div>
                  <div className="text-sm font-bold text-white">
                    {driver1Stats.name}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono">
                    #{driver1Stats.number}
                  </div>
                </div>
              </div>
              {driver1Stats.teams && driver1Stats.teams.length > 0 && (
                <div className="text-xs text-gray-400 font-mono">
                  {driver1Stats.teams[driver1Stats.teams.length - 1].team?.name}
                </div>
              )}
            </div>

            {/* Driver 2 Card */}
            <div
              className="p-3 border rounded-sm"
              style={{
                backgroundColor: `${driver2Color}10`,
                borderColor: `${driver2Color}40`
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ backgroundColor: driver2Color }}
                />
                <div>
                  <div className="text-sm font-bold text-white">
                    {driver2Stats.name}
                  </div>
                  <div className="text-[10px] text-gray-500 font-mono">
                    #{driver2Stats.number}
                  </div>
                </div>
              </div>
              {driver2Stats.teams && driver2Stats.teams.length > 0 && (
                <div className="text-xs text-gray-400 font-mono">
                  {driver2Stats.teams[driver2Stats.teams.length - 1].team?.name}
                </div>
              )}
            </div>
          </div>

          {/* Stats Comparison */}
          <div className="p-4 text-xs font-mono">
            {/* Career Stats Section */}
            <div className="mb-4">
              <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-3 font-bold">
                Career Statistics
              </div>
              <StatRow
                label="World Championships"
                val1={driver1Stats.world_championships}
                val2={driver2Stats.world_championships}
              />
              <StatRow
                label="Grands Prix"
                val1={driver1Stats.grands_prix_entered}
                val2={driver2Stats.grands_prix_entered}
              />
              <StatRow
                label="Podiums"
                val1={driver1Stats.podiums}
                val2={driver2Stats.podiums}
              />
              <StatRow
                label="Career Points"
                val1={parseFloat(driver1Stats.career_points).toFixed(1)}
                val2={parseFloat(driver2Stats.career_points).toFixed(1)}
              />
            </div>

            {/* Best Results Section */}
            <div className="mb-4">
              <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-3 font-bold">
                Best Results
              </div>
              <StatRow
                label="Highest Finish"
                val1={driver1Stats.highest_race_finish?.position || '—'}
                val2={driver2Stats.highest_race_finish?.position || '—'}
                higherIsBetter={false}
              />
              <StatRow
                label="Highest Grid"
                val1={driver1Stats.highest_grid_position || '—'}
                val2={driver2Stats.highest_grid_position || '—'}
                higherIsBetter={false}
              />
            </div>

            {/* Personal Info Section */}
            <div>
              <div className="text-[10px] text-gray-600 uppercase tracking-wider mb-3 font-bold">
                Personal
              </div>
              <div className="grid grid-cols-3 gap-4 py-3 border-b border-white/5">
                <div className="text-right text-gray-400">
                  {driver1Stats.nationality || driver1Stats.country || '—'}
                </div>
                <div className="text-center">
                  <span className="text-[10px] text-gray-600 uppercase tracking-wider font-mono">
                    Nationality
                  </span>
                </div>
                <div className="text-left text-gray-400">
                  {driver2Stats.nationality || driver2Stats.country || '—'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 py-3">
                <div className="text-right text-gray-400 text-[11px]">
                  {driver1Stats.birthdate ? new Date(driver1Stats.birthdate).getFullYear() : '—'}
                </div>
                <div className="text-center">
                  <span className="text-[10px] text-gray-600 uppercase tracking-wider font-mono">
                    Birth Year
                  </span>
                </div>
                <div className="text-left text-gray-400 text-[11px]">
                  {driver2Stats.birthdate ? new Date(driver2Stats.birthdate).getFullYear() : '—'}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Footer */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="text-[10px] text-gray-600 font-mono uppercase tracking-wider text-center">
          Head to Head Comparison
        </div>
      </div>
    </div>
  );
};

export default F1DriverComparison;
